/**
 * P2P file sharing over WebRTC data channels during a call.
 *
 * The admin (wallet-authenticated meeting host) can share a file with every
 * call participant. The file goes DIRECTLY peer-to-peer over the already
 * established call connections — it never touches the orchestrator or the
 * Dwinity backend. Guests receive the bytes, assemble a Blob and download it
 * to their own machine.
 *
 * The sender chooses a share mode: 'download' (recipient gets a download
 * button) or 'preview' (previewable types — image/video/audio/PDF — are shown
 * inline on the call surface, with download still available).
 *
 * Protocol (per data channel, one incoming transfer at a time per sender):
 *   1. JSON control message: { t: 'meta', id, name, size, mime, mode }
 *   2. N binary chunks (ArrayBuffer, CHUNK_SIZE bytes each)
 *   3. JSON control message: { t: 'done', id }
 *   Sender may abort with { t: 'abort', id }.
 */
export const FILE_SHARE_CHANNEL_LABEL = 'fileshare';
/** Negotiated channel id — both sides create the channel with the same id,
 *  so it exists regardless of which peer was the SDP offerer. */
export const FILE_SHARE_CHANNEL_ID = 0;
/** 50 MB limit — agreed product decision (fast on any network). */
export const MAX_FILE_SHARE_BYTES = 50 * 1024 * 1024;
/** Bound per-chunk bookkeeping even if a hostile peer sends tiny chunks. */
export const MAX_FILE_SHARE_CHUNKS = Math.ceil(MAX_FILE_SHARE_BYTES / 1024);
const CHUNK_SIZE = 16 * 1024; // 16 KB — safe across browsers
// Keep the SCTP send buffer SMALL so file chunks never queue up ahead of the
// audio/video packets sharing the same peer connection. A big backlog lets the
// data channel saturate the link and starve the media stream.
const HIGH_WATER_MARK = 128 * 1024; // pause sending above 128 KB buffered
const LOW_WATER_MARK = 64 * 1024;
// Pace the transfer so media keeps priority: send a small burst, then yield.
// ~4 chunks (64 KB) per ~40 ms tick ≈ 1.6 MB/s per recipient — a 50 MB file
// still completes in ~30 s while leaving headroom for the call itself.
const BURST_CHUNKS = 4;
const PACE_INTERVAL_MS = 40;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
/** Can this mime type be previewed inline on the call surface? */
export function isPreviewableMime(mime) {
    return /^(image|video|audio)\//.test(mime) || mime === 'application/pdf';
}
/** Never trust the sender's declared byte budget (including NaN/fractions). */
export function validFileShareSize(size) {
    return typeof size === 'number' && Number.isSafeInteger(size) && size >= 0 && size <= MAX_FILE_SHARE_BYTES;
}
export function validFileShareMeta(msg) {
    if (!msg || typeof msg !== 'object')
        return false;
    const m = msg;
    return m.t === 'meta' && typeof m.id === 'string' && m.id.length > 0 &&
        typeof m.name === 'string' && typeof m.mime === 'string' &&
        (m.mode === undefined || m.mode === 'download' || m.mode === 'preview') &&
        validFileShareSize(m.size);
}
export function createFileShareChannel(pc) {
    const dc = pc.createDataChannel(FILE_SHARE_CHANNEL_LABEL, {
        negotiated: true,
        id: FILE_SHARE_CHANNEL_ID,
        ordered: true,
    });
    dc.binaryType = 'arraybuffer';
    return dc;
}
/** Attach a receiver to a channel. Calls onUpdate with transfer snapshots. */
export function attachFileShareReceiver(dc, from, onUpdate) {
    let incoming = null;
    const failIncoming = () => {
        if (!incoming)
            return;
        const { meta, received } = incoming;
        incoming = null;
        onUpdate({
            id: meta.id, name: meta.name, size: meta.size, mime: meta.mime, mode: meta.mode || 'download',
            from, direction: 'in', progress: received / Math.max(meta.size, 1), status: 'error',
        });
    };
    dc.onclose = failIncoming;
    dc.onerror = failIncoming;
    dc.onmessage = (e) => {
        if (typeof e.data === 'string') {
            let parsed;
            try {
                parsed = JSON.parse(e.data);
            }
            catch {
                return;
            }
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
                return;
            const msg = parsed;
            if (msg.t === 'meta') {
                failIncoming(); // a new meta replaces, rather than leaks, the previous transfer
                if (!validFileShareMeta(msg)) {
                    if (typeof msg.id === 'string' && typeof msg.name === 'string' && typeof msg.mime === 'string' &&
                        typeof msg.size === 'number' && Number.isFinite(msg.size) && msg.size > MAX_FILE_SHARE_BYTES) {
                        onUpdate({
                            id: msg.id, name: msg.name, size: msg.size, mime: msg.mime, mode: 'download',
                            from, direction: 'in', progress: 0, status: 'error', errorReason: 'too_large',
                        });
                    }
                    return;
                }
                incoming = { meta: msg, chunks: [], received: 0 };
                onUpdate({
                    id: msg.id, name: msg.name, size: msg.size, mime: msg.mime, mode: msg.mode || 'download',
                    from, direction: 'in', progress: 0, status: 'transferring',
                });
            }
            else if (msg.t === 'done' && incoming && incoming.meta.id === msg.id) {
                const { meta, chunks, received } = incoming;
                incoming = null;
                if (received !== meta.size) {
                    onUpdate({
                        id: meta.id, name: meta.name, size: meta.size, mime: meta.mime, mode: meta.mode || 'download',
                        from, direction: 'in', progress: received / Math.max(meta.size, 1), status: 'error',
                    });
                    return;
                }
                const blob = new Blob(chunks, { type: meta.mime || 'application/octet-stream' });
                onUpdate({
                    id: meta.id, name: meta.name, size: meta.size, mime: meta.mime, mode: meta.mode || 'download',
                    from, direction: 'in', progress: 1, status: 'done',
                    blobUrl: URL.createObjectURL(blob),
                });
            }
            else if (msg.t === 'abort' && incoming && incoming.meta.id === msg.id) {
                const { meta } = incoming;
                incoming = null;
                onUpdate({
                    id: meta.id, name: meta.name, size: meta.size, mime: meta.mime, mode: meta.mode || 'download',
                    from, direction: 'in', progress: 0, status: 'error',
                });
            }
        }
        else if (incoming && e.data instanceof ArrayBuffer) {
            const buf = e.data;
            if (buf.byteLength === 0)
                return;
            if (buf.byteLength > incoming.meta.size - incoming.received ||
                incoming.chunks.length >= MAX_FILE_SHARE_CHUNKS) {
                failIncoming();
                return;
            }
            incoming.chunks.push(buf);
            incoming.received += buf.byteLength;
            const { meta, received } = incoming;
            // throttle progress updates to ~every 512 KB
            if (received === meta.size || received % (512 * 1024) < CHUNK_SIZE) {
                onUpdate({
                    id: meta.id, name: meta.name, size: meta.size, mime: meta.mime, mode: meta.mode || 'download',
                    from, direction: 'in', progress: received / Math.max(meta.size, 1), status: 'transferring',
                });
            }
        }
    };
}
/** Wait until the channel's buffer drains. Never hangs: resolves on close /
 *  error and after a timeout; the caller re-checks readyState afterwards. */
function waitForDrain(dc) {
    if (dc.bufferedAmount <= HIGH_WATER_MARK || dc.readyState !== 'open')
        return Promise.resolve();
    return new Promise((resolve) => {
        const prev = dc.bufferedAmountLowThreshold;
        let timer;
        const finish = () => {
            clearTimeout(timer);
            dc.removeEventListener('bufferedamountlow', finish);
            dc.removeEventListener('close', finish);
            dc.removeEventListener('error', finish);
            dc.bufferedAmountLowThreshold = prev;
            resolve();
        };
        dc.bufferedAmountLowThreshold = LOW_WATER_MARK;
        dc.addEventListener('bufferedamountlow', finish);
        dc.addEventListener('close', finish);
        dc.addEventListener('error', finish);
        timer = setTimeout(finish, 15_000); // stalled channel — give up waiting
    });
}
/**
 * Send a file to every open channel. Progress reflects the SLOWEST recipient
 * (total bytes queued across channels / total to send).
 */
export async function sendFileOverChannels(channels, file, onUpdate, mode = 'download') {
    const id = `fs-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const open = [...channels.values()].filter((dc) => dc.readyState === 'open');
    const base = {
        id, name: file.name, size: file.size, mime: file.type, mode,
        from: '', direction: 'out',
    };
    if (!validFileShareSize(file.size)) {
        onUpdate({ ...base, progress: 0, status: 'error', errorReason: 'too_large' });
        return;
    }
    if (open.length === 0) {
        onUpdate({ ...base, progress: 0, status: 'error' });
        return;
    }
    const meta = { t: 'meta', id, name: file.name, size: file.size, mime: file.type, mode };
    open.forEach((dc) => dc.send(JSON.stringify(meta)));
    onUpdate({ ...base, progress: 0, status: 'transferring' });
    try {
        // channels still participating — a closed/stalled peer is dropped from the
        // set so it cannot block the others
        let active = open;
        let offset = 0;
        let burst = 0;
        while (offset < file.size) {
            const chunk = await file.slice(offset, offset + CHUNK_SIZE).arrayBuffer();
            for (const dc of active) {
                if (dc.readyState !== 'open')
                    continue;
                await waitForDrain(dc);
                if (dc.readyState !== 'open')
                    continue; // closed while waiting
                try {
                    dc.send(chunk);
                }
                catch { /* dropped below */ }
            }
            active = active.filter((dc) => dc.readyState === 'open');
            if (active.length === 0)
                throw new Error('all recipients disconnected');
            offset += chunk.byteLength;
            if (offset === file.size || offset % (512 * 1024) < CHUNK_SIZE) {
                onUpdate({ ...base, progress: offset / Math.max(file.size, 1), status: 'transferring' });
            }
            // Pacing: yield between bursts so the media stream on the same peer
            // connection keeps getting bandwidth (file transfer must not starve it).
            burst += 1;
            if (burst >= BURST_CHUNKS && offset < file.size) {
                burst = 0;
                await sleep(PACE_INTERVAL_MS);
            }
        }
        open.forEach((dc) => {
            try {
                if (dc.readyState === 'open')
                    dc.send(JSON.stringify({ t: 'done', id }));
            }
            catch { /* ignore */ }
        });
        // The sender keeps a local object URL too, so the admin sees (and can
        // re-open) exactly what was shared — mirrors the receivers' view.
        onUpdate({ ...base, progress: 1, status: 'done', blobUrl: URL.createObjectURL(file) });
    }
    catch (err) {
        console.error('[fileShare] send failed:', err);
        open.forEach((dc) => {
            try {
                if (dc.readyState === 'open')
                    dc.send(JSON.stringify({ t: 'abort', id }));
            }
            catch { /* ignore */ }
        });
        onUpdate({ ...base, progress: 0, status: 'error' });
    }
}
